"use client";

import { useEffect } from "react";

import { Button } from "@/components/ui/button";
import { Eyebrow } from "@/components/ui/eyebrow";
import { getMessages } from "@/lib/messages";

/**
 * The route-level error boundary for every public route.
 *
 * This is the surface that replaced the mock fallback. `lib/api.ts` used to catch a failed
 * backend fetch and return mock data, so a real 500 rendered a fabricated price list; now the
 * fetch throws, Next unwinds to here, and the visitor is told the truth. It sits directly
 * under the root layout, so the header and footer stay rendered and the site is still
 * navigable — the failure is scoped to the page body.
 *
 * Errors thrown in the root layout itself are not caught here (Next's boundary rules) — but
 * the layout does no data fetching, so it has nothing to throw.
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const messages = getMessages();

  useEffect(() => {
    // The visitor gets a generic message; the operator gets the real one. In production Next
    // replaces `error.message` with a digest, which is the value to grep the server log for.
    console.error("[route error]", error.digest ?? "", error.message);
  }, [error]);

  return (
    <div className="container-xfit flex flex-col items-start gap-4 py-band-lg">
      <Eyebrow tone="signal">Error</Eyebrow>
      <h1 className="display-wide text-display-l font-bold">{messages.error.title}</h1>
      <p className="prose-column text-body-l text-ink-2">{messages.error.body}</p>
      {error.digest && (
        <p className="font-mono text-caption text-muted">
          Reference: <code>{error.digest}</code>
        </p>
      )}
      <div className="mt-4 flex flex-wrap gap-3">
        <Button onClick={reset}>{messages.error.retry}</Button>
        <Button href="/" variant="secondary">
          {messages.error.homeCta}
        </Button>
      </div>
    </div>
  );
}
